/**
 * Phase 19.1 — Actual Rent History Upload Modal
 * ==============================================
 * Lets owners of a property upload year-by-year actual rent collected so the
 * Performance tab's IRR flips from "appreciation-only CAGR" to a proper
 * Newton-method IRR on the real cash-flow stream.
 *
 * Two input modes (tabs):
 *   1. Type values manually — one row per year between purchase_year and
 *      current_year-1. Default: prefilled with `monthly_rent × 12`.
 *   2. Upload CSV — a 2-column file (year, rent_collected). Headers
 *      optional; the first numeric row decides if there's a header.
 *
 * On Save: POST /api/deals/{id}/rent-history → toast with the new IRR.
 */
import React, { useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SAMPLE_CSV =
  "year,rent_collected\n" +
  "2019,24000\n" +
  "2020,25200\n" +
  "2021,26400\n" +
  "2022,27600\n" +
  "2023,28800\n" +
  "2024,30000\n";

function parseCsv(text) {
  // Permissive CSV: split on newlines, strip BOM + quotes, infer header,
  // accept commas / semicolons / tabs. Year must parse as int; rent as
  // float. Skip rows where either fails.
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return [];
  const rows = lines.map((l) => l.split(/[,;\t]/).map((c) => c.trim().replace(/^["']|["']$/g, "")));
  // Decide if first row is a header — true if first cell isn't a valid year.
  const firstRowFirstCol = parseInt(rows[0][0], 10);
  const startIdx = isFinite(firstRowFirstCol) && firstRowFirstCol > 1900 ? 0 : 1;
  const out = [];
  for (let i = startIdx; i < rows.length; i++) {
    const [yRaw, rRaw] = rows[i];
    const y = parseInt(yRaw, 10);
    const r = parseFloat((rRaw || "").replace(/[$,\s]/g, ""));
    if (!isFinite(y) || y < 1950 || y > 2099) continue;
    if (!isFinite(r) || r < 0) continue;
    out.push({ year: y, rent_collected: r });
  }
  return out;
}

export default function RentHistoryModal({ open, onOpenChange, deal, onSaved }) {
  const purchaseYear = deal?.purchase_year;
  const currentYear = new Date().getFullYear();
  const yearsRange = useMemo(() => {
    if (!purchaseYear) return [];
    const yrs = [];
    for (let y = purchaseYear; y <= currentYear; y++) yrs.push(y);
    return yrs;
  }, [purchaseYear, currentYear]);

  const defaultMonthly = deal?.monthly_rent || 0;

  const [tab, setTab] = useState("manual");
  // Manual entry — map of year → rent string for in-progress edits
  const [manual, setManual] = useState(() => {
    // Hydrate from existing rent_history_actual if present, else seed with
    // current_monthly_rent × 12 to give the user a starting point.
    const existing = deal?.rent_history_actual || [];
    const map = {};
    for (const yr of yearsRange) {
      const hit = existing.find((e) => e.year === yr);
      map[yr] = hit ? String(hit.rent_collected) : String(defaultMonthly * 12);
    }
    return map;
  });
  const [parsedCsv, setParsedCsv] = useState([]);
  const [csvError, setCsvError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCsv(String(e.target?.result || ""));
        if (parsed.length === 0) {
          setCsvError("No valid rows found in this CSV.");
          setParsedCsv([]);
          return;
        }
        setParsedCsv(parsed);
        setCsvError(null);
      } catch (err) {
        setCsvError(err?.message || "Failed to parse CSV");
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "runidea-rent-history-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const submit = async () => {
    const entries = tab === "manual"
      ? Object.entries(manual)
        .map(([y, r]) => ({ year: Number(y), rent_collected: Number(r) }))
        .filter((e) => isFinite(e.year) && isFinite(e.rent_collected) && e.rent_collected > 0)
      : parsedCsv;

    if (entries.length === 0) {
      toast.error("Add at least one year with a rent amount");
      return;
    }
    const previousIrr = deal?.historical_performance?.irr_to_date_pct;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/rent-history`, { entries });
      const newIrr = data?.irr_to_date_pct;
      const oldPct = previousIrr != null ? `${(previousIrr * 100).toFixed(1)}%` : "—";
      const newPct = newIrr != null ? `${(newIrr * 100).toFixed(1)}%` : "—";
      toast.success(`IRR refined: ${oldPct} → ${newPct} with actual data`);
      if (onSaved) onSaved(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message
        || (Array.isArray(detail) ? detail[0]?.msg : null)
        || e?.message
        || "Upload failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rent-history-modal">
        <DialogHeader>
          <DialogTitle>Upload actual rent history</DialogTitle>
          <DialogDescription>
            One number per year, {purchaseYear || "—"}–{currentYear}. We'll
            replace the smoothed estimate and recompute IRR using real cash flows.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="manual" data-testid="rent-history-tab-manual">Type values</TabsTrigger>
            <TabsTrigger value="csv" data-testid="rent-history-tab-csv">Upload CSV</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-3 max-h-72 overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {yearsRange.map((yr) => (
                <div key={yr}>
                  <Label className="text-xs text-slate-500">{yr}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={manual[yr] ?? ""}
                    onChange={(e) => setManual((m) => ({ ...m, [yr]: e.target.value }))}
                    data-testid={`rent-history-input-${yr}`}
                    className="mt-1 tabular-nums"
                  />
                </div>
              ))}
            </div>
            {yearsRange.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">
                Set <strong>purchase year</strong> on the deal first.
              </p>
            ) : null}
          </TabsContent>

          <TabsContent value="csv" className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                data-testid="rent-history-csv-pick"
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Choose CSV file
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={downloadTemplate}
                data-testid="rent-history-csv-template"
              >
                <FileDown className="mr-1.5 h-3.5 w-3.5" />
                Download template
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="rent-history-csv-input"
            />
            {csvError ? (
              <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="rent-history-csv-error">{csvError}</p>
            ) : null}
            {parsedCsv.length > 0 ? (
              <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs" data-testid="rent-history-csv-preview">
                <p className="font-semibold text-slate-700">{parsedCsv.length} row{parsedCsv.length === 1 ? "" : "s"} ready to upload:</p>
                <ul className="mt-1 space-y-0.5">
                  {parsedCsv.slice(0, 12).map((r) => (
                    <li key={r.year} className="font-mono text-slate-600">
                      {r.year} · ${r.rent_collected.toLocaleString()}
                    </li>
                  ))}
                  {parsedCsv.length > 12 ? <li className="text-slate-400">…{parsedCsv.length - 12} more</li> : null}
                </ul>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rent-history-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="rent-history-save"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {submitting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Save &amp; recompute IRR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
